import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  FileBarChart, 
  Building2, 
  Calendar,
  Download,
  TrendingUp,
  Users,
  Receipt,
  Clock,
  Percent
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { propertiesAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

const FinancialStatements = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [statementData, setStatementData] = useState(null);
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Generate year options (last 5 years) - memoized
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  }, []);

  const fetchProperties = useCallback(async () => {
    try {
      const response = await propertiesAPI.getAll({ transaction_type: 'location' });
      setProperties(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des biens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const fetchStatement = async () => {
    if (!selectedProperty || !selectedYear) {
      toast.error('Veuillez sélectionner un bien et une année');
      return;
    }

    setLoadingStatement(true);
    try {
      const response = await propertiesAPI.getFinancialStatement(selectedProperty, selectedYear);
      setStatementData(response.data);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du chargement du relevé';
      toast.error(message);
      setStatementData(null);
    } finally {
      setLoadingStatement(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedProperty || !selectedYear) {
      toast.error('Veuillez sélectionner un bien et une année');
      return;
    }

    setDownloading(true);
    try {
      toast.info('Génération du PDF en cours...');
      const response = await propertiesAPI.downloadFinancialStatement(selectedProperty, selectedYear);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const property = properties.find(p => p.id === selectedProperty);
      const propertyCode = property?.property_code || selectedProperty.slice(0, 8);
      link.download = `releve_financier_${propertyCode}_${selectedYear}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Relevé financier téléchargé');
    } catch (error) {
      let message = 'Erreur lors du téléchargement';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          message = errorData.detail || message;
        } catch (parseError) {
          console.error('Error parsing blob response:', parseError);
          // Unable to parse blob response
        }
      }
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const monthNames = {
    '01': 'Janvier', '02': 'Février', '03': 'Mars', '04': 'Avril',
    '05': 'Mai', '06': 'Juin', '07': 'Juillet', '08': 'Août',
    '09': 'Septembre', '10': 'Octobre', '11': 'Novembre', '12': 'Décembre'
  };

  return (
    <div className="space-y-6" data-testid="financial-statements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            Relevés Financiers
          </h1>
          <p className="text-gray-500 mt-1">
            Générez des relevés financiers annuels par bien immobilier
          </p>
        </div>
      </div>

      {/* Selection Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileBarChart className="w-5 h-5 text-[#1B3A4B]" />
            Générer un Relevé
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Bien immobilier
              </label>
              <Select
                value={selectedProperty}
                onValueChange={setSelectedProperty}
              >
                <SelectTrigger data-testid="property-select">
                  <SelectValue placeholder="Sélectionnez un bien" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((property) => (
                    <SelectItem key={property.id} value={property.id}>
                      {property.title} ({property.property_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Année
              </label>
              <Select
                value={selectedYear}
                onValueChange={setSelectedYear}
              >
                <SelectTrigger data-testid="year-select">
                  <SelectValue placeholder="Année" />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end gap-2">
              <Button
                onClick={fetchStatement}
                disabled={!selectedProperty || !selectedYear || loadingStatement}
                className="bg-[#1B3A4B] hover:bg-[#142D3A] flex-1"
                data-testid="generate-statement-btn"
              >
                {loadingStatement ? 'Chargement...' : 'Afficher'}
              </Button>
              <Button
                onClick={handleDownloadPDF}
                disabled={!selectedProperty || !selectedYear || downloading}
                variant="outline"
                className="border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373]/10"
                data-testid="download-statement-btn"
              >
                <Download className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statement Results */}
      {statementData && (
        <div className="space-y-6">
          {/* Payment Rate KPI - base sur paiements de loyer */}
          <Card className="kpi-premium rise-in" data-testid="payment-rate-card">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl shrink-0 shadow-inner" style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #FEF3C7 100%)' }}>
                  <TrendingUp className="w-5 h-5 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1B3A4B]">
                    Taux de paiement annuel <span className="text-[11px] text-gray-400 font-normal">(basé sur les paiements de loyer confirmés)</span>
                  </p>
                  <div className="flex items-baseline gap-3 mt-2 flex-wrap">
                    <p className="text-4xl font-extrabold tabular-nums text-gradient-gold" style={{ lineHeight: 1 }} data-testid="payment-rate-percentage">
                      {statementData.payment_rate_percentage ?? 0}%
                    </p>
                    <p className="text-sm text-gray-500 tabular-nums">
                      {formatCurrency(statementData.total_received, 'XOF')} <span className="text-gray-300">/</span> {formatCurrency(statementData.total_expected || 0, 'XOF')}
                    </p>
                  </div>
                  <div className="mt-3 h-2.5 bg-slate-100 rounded-full overflow-hidden relative">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${Math.min(statementData.payment_rate_percentage ?? 0, 100)}%`,
                        background: 'linear-gradient(90deg, #10B981 0%, #D4A745 60%, #F2A900 100%)',
                      }}
                    />
                    <div className="absolute inset-0 progress-shimmer rounded-full" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Encaissé</p>
                    <p className="text-lg font-semibold text-green-600">
                      {formatCurrency(statementData.total_received, 'XOF')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">En Attente</p>
                    <p className="text-lg font-semibold text-orange-600">
                      {formatCurrency(statementData.total_pending, 'XOF')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Receipt className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Paiements</p>
                    <p className="text-lg font-semibold text-blue-600">
                      {statementData.payment_count}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Percent className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Taux Commission</p>
                    <p className="text-lg font-semibold text-gray-600">
                      {statementData.commission_rate || 5} %
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-purple-600">Commission (Encaissé)</p>
                    <p className="text-lg font-semibold text-purple-600">
                      {formatCurrency(statementData.commission_on_received || 0, 'XOF')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Locataires</p>
                    <p className="text-lg font-semibold text-indigo-600">
                      {statementData.tenants?.length || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tenants Table */}
          {statementData.tenants?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#1B3A4B]" />
                  Locataires Concernés
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Code</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Nom</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Paiements</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Encaissé</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">En Attente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statementData.tenants.map((tenant) => (
                        <tr key={tenant.tenant_id || tenant.tenant_code} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-mono text-sm text-[#1B3A4B]">
                            {tenant.tenant_code}
                          </td>
                          <td className="p-3 font-medium">{tenant.tenant_name}</td>
                          <td className="p-3 text-center">
                            <Badge variant="outline">{tenant.payment_count}</Badge>
                          </td>
                          <td className="p-3 text-right text-green-600 font-medium">
                            {formatCurrency(tenant.total_received, 'XOF')}
                          </td>
                          <td className="p-3 text-right text-orange-600">
                            {formatCurrency(tenant.total_pending, 'XOF')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Monthly Summary */}
          {Object.keys(statementData.monthly_summary || {}).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#D4A373]" />
                  Récapitulatif Mensuel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-[#D4A373]/10">
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Mois</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">Encaissé</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600">En Attente</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Nb Paiements</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(statementData.monthly_summary)
                        .filter(([key]) => key !== 'unknown')
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([monthKey, data]) => {
                          const monthNum = monthKey.split('-')[1] || '01';
                          const monthName = monthNames[monthNum] || monthKey;
                          return (
                            <tr key={monthKey} className="border-b hover:bg-gray-50">
                              <td className="p-3 font-medium">{monthName}</td>
                              <td className="p-3 text-right text-green-600 font-medium">
                                {formatCurrency(data.received, 'XOF')}
                              </td>
                              <td className="p-3 text-right text-orange-600">
                                {formatCurrency(data.pending, 'XOF')}
                              </td>
                              <td className="p-3 text-center">
                                <Badge variant="outline">{data.count}</Badge>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Data Message */}
          {statementData.payment_count === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <FileBarChart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600">Aucun paiement trouvé</h3>
                <p className="text-gray-400 mt-1">
                  Aucun paiement enregistré pour ce bien en {selectedYear}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Initial State */}
      {!statementData && !loadingStatement && (
        <Card>
          <CardContent className="py-16 text-center">
            <FileBarChart className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-500">Sélectionnez un bien et une année</h3>
            <p className="text-gray-400 mt-2">
              Choisissez un bien immobilier et une année pour générer le relevé financier
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FinancialStatements;
